import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Gift, Check, RefreshCw, Users, DollarSign } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * ReferralsPanel — Admin › Referrals
 *
 * Shows every friend-referral that redeemed a $5 discount. Owner marks
 * each row "paid" once they've handed the referrer their $5 credit
 * (via loyalty account, next-order voucher, or a straight Venmo).
 */
export default function ReferralsPanel() {
  const [data, setData] = useState({ items: [], count: 0, pending_count: 0, pending_total_cents: 0 });
  const [loading, setLoading] = useState(false);

  const fetchReferrals = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/referrals`);
      setData(res.data);
    } catch (err) {
      console.warn("Failed to load referrals", err?.response?.status || err?.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchReferrals(); }, [fetchReferrals]);

  const markPaid = async (id) => {
    if (!window.confirm("Mark this referral credit as paid?")) return;
    try {
      await axios.post(`${API}/admin/referrals/${id}/mark-paid`);
      await fetchReferrals();
    } catch (err) {
      alert("Failed: " + (err?.response?.data?.detail || err.message));
    }
  };

  const { items, count, pending_count, pending_total_cents } = data;

  return (
    <section data-testid="referrals-panel" className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Gift size={16} className="text-[var(--mm-accent)]" />
          <h2 className="section-label">Refer-a-Friend Rewards · {count}</h2>
        </div>
        <button
          onClick={fetchReferrals}
          data-testid="referrals-refresh"
          className="inline-flex items-center gap-1.5 border border-[var(--mm-line)] text-[var(--mm-cream)] px-3 py-2 text-xs tracking-[0.2em] uppercase hover:border-[var(--mm-accent)] hover:text-[var(--mm-accent)] transition-colors"
        >
          <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <div className="border border-[var(--mm-line)] p-4">
          <div className="text-[10px] tracking-[0.24em] uppercase text-[var(--mm-cream-muted)] mb-2 flex items-center gap-2">
            <Users size={11} /> Redemptions
          </div>
          <div className="font-display italic text-3xl text-[var(--mm-cream)]">{count}</div>
        </div>
        <div className="border border-[var(--mm-line)] p-4">
          <div className="text-[10px] tracking-[0.24em] uppercase text-[var(--mm-cream-muted)] mb-2 flex items-center gap-2">
            <Gift size={11} /> Pending payouts
          </div>
          <div className="font-display italic text-3xl text-amber-400">{pending_count}</div>
        </div>
        <div className="border border-[var(--mm-line)] p-4">
          <div className="text-[10px] tracking-[0.24em] uppercase text-[var(--mm-cream-muted)] mb-2 flex items-center gap-2">
            <DollarSign size={11} /> Owed to referrers
          </div>
          <div className="font-display italic text-3xl text-[var(--mm-accent)]">
            ${(pending_total_cents / 100).toFixed(2)}
          </div>
        </div>
      </div>

      {/* Table */}
      {items.length === 0 ? (
        <div className="border border-dashed border-[var(--mm-line)] p-10 text-center text-[var(--mm-cream-muted)]">
          <Gift className="mx-auto mb-3 text-[var(--mm-accent)]" size={22} />
          <p className="font-display italic text-xl text-[var(--mm-cream)] mb-1">No referrals yet</p>
          <p className="text-xs">Every welcomed customer gets their own share link — this list fills as friends redeem $5 off.</p>
        </div>
      ) : (
        <div className="border border-[var(--mm-line)] overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[var(--mm-bg-alt)]">
              <tr className="text-left text-[10px] tracking-[0.22em] uppercase text-[var(--mm-cream-muted)]">
                <th className="px-3 py-2">Referrer</th>
                <th className="px-3 py-2">Code</th>
                <th className="px-3 py-2">Friend</th>
                <th className="px-3 py-2">Owed</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Date</th>
                <th className="px-3 py-2 w-24"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr
                  key={r.id}
                  data-testid={`referral-row-${r.id}`}
                  className="border-t border-[var(--mm-line)] hover:bg-[var(--mm-bg-alt)]/40"
                >
                  <td className="px-3 py-2 text-[var(--mm-cream)]">{r.referrer_email}</td>
                  <td className="px-3 py-2 font-mono text-[var(--mm-accent)] text-xs">{r.code}</td>
                  <td className="px-3 py-2 text-[var(--mm-cream-muted)]">{r.referee_email || "—"}</td>
                  <td className="px-3 py-2 text-[var(--mm-accent)] font-semibold">
                    ${((r.credit_cents || 0) / 100).toFixed(2)}
                  </td>
                  <td className="px-3 py-2">
                    <span
                      className={`text-[10px] tracking-[0.2em] uppercase font-semibold ${
                        r.credit_status === "paid"
                          ? "text-emerald-400"
                          : "text-amber-400"
                      }`}
                    >
                      {r.credit_status}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-[var(--mm-cream-muted)] text-xs">
                    {new Date(r.created_at).toLocaleDateString("en-AU")}
                  </td>
                  <td className="px-3 py-2">
                    {r.credit_status === "pending" ? (
                      <button
                        onClick={() => markPaid(r.id)}
                        data-testid={`referral-mark-paid-${r.id}`}
                        className="inline-flex items-center gap-1 text-[10px] tracking-[0.2em] uppercase text-emerald-400 hover:text-emerald-300"
                      >
                        <Check size={11} /> Mark paid
                      </button>
                    ) : (
                      <span className="text-[10px] tracking-[0.2em] uppercase text-[var(--mm-cream-muted)]">✓ Paid</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="border-t border-[var(--mm-line)] pt-6 text-xs text-[var(--mm-cream-muted)] leading-relaxed max-w-2xl">
        <span className="font-semibold text-[var(--mm-cream)] tracking-[0.15em] uppercase text-[10px]">
          How it works ·
        </span>{" "}
        Every welcome-email recipient gets a personal share link like{" "}
        <code className="text-[var(--mm-accent)]">mamaminassoulfeed.online/?ref=MM4XK7</code>.
        When their friend orders, we knock $5 off automatically and pin a
        pending row here so you can drop the referrer their $5 credit
        (next-order voucher, loyalty top-up, or cash).
      </div>
    </section>
  );
}
